import type { AccountType, InstitutionType } from "@verity/database/prisma";
import { CoreMenuItem, CoreSelect, CoreSelectProps } from "../../core";
import InstitutionBaseRow, { type EditConsumerProps, type ViewConsumerProps } from "./InstitutionBaseRow";
import { AccountTypeSchema, InstitutionTypeSchema } from "@verity/database/zod";
import { ListItemText } from "@mui/material";
import { useInstitutionRowHeader, getDisabledProps } from "./informationHelper";
import type { YujaAccount } from "@yujacorp/verity-api-contract";
import { useState } from "react";
import styles from "./InstitutionBaseRow.module.css";

type InstitutionDropdownProps<T> = {
	value: T;
	placeholder?: string;
	onChange?: (value: T) => void;
} & Pick<
	CoreSelectProps<T>,
	| "aria-labelledby"
	| "renderValue"
	| "disabled"
	| "disabledTooltip"
	| "searchProps"
	| "children"
	| "unselectedPlaceholder"
>;

function InstitutionDropdown<T>({ value, onChange, ...props }: InstitutionDropdownProps<T>) {
	return (
		<CoreSelect
			{...props}
			value={value}
			onChange={(e) => onChange?.(e.target.value as T)}
			className={styles.institutionDropdown}
		/>
	);
}

type EditProductRowProps = EditConsumerProps & {
	product: InstitutionType;
	onChange: (value: InstitutionType) => void;
};
type ViewProductRowProps = ViewConsumerProps & {
	product: InstitutionType;
};

type InstitutionProductRowProps = EditProductRowProps | ViewProductRowProps;

export function InstitutionProductRow({ product, ...props }: InstitutionProductRowProps) {
	const { t, commonProps } = useInstitutionRowHeader("product", props);

	if (props.mode === "edit") {
		return (
			<InstitutionBaseRow
				{...commonProps}
				strategy={{
					mode: "edit",
					required: true,
					createControl: (id) => (
						<InstitutionDropdown<InstitutionType>
							value={product}
							onChange={props.onChange}
							aria-labelledby={id}
							{...getDisabledProps(props)}
							renderValue={(value) => {
								const renderValue = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
								if (value === "candidate") return t("AI", { name: renderValue });
								return renderValue;
							}}
						>
							{InstitutionTypeSchema.options.map((option) => (
								<CoreMenuItem key={option} value={option}>
									<ListItemText primary={t(option)} />
								</CoreMenuItem>
							))}
						</InstitutionDropdown>
					),
				}}
			/>
		);
	}

	return (
		<InstitutionBaseRow
			{...commonProps}
			strategy={{
				mode: "view",
				value: product === "candidate" ? t("candidate") : t("verity"),
				copyAriaLabel: t("copyAriaLabel"),
			}}
		/>
	);
}

type EditAccountTypeRowProps = EditConsumerProps & {
	accountType: AccountType;
	onChange: (value: AccountType) => void;
};
type ViewAccountTypeRowProps = ViewConsumerProps & {
	accountType: AccountType;
};

type InstitutionAccountTypeRowProps = EditAccountTypeRowProps | ViewAccountTypeRowProps;

export function InstitutionAccountTypeRow({ accountType, ...props }: InstitutionAccountTypeRowProps) {
	const { t, commonProps } = useInstitutionRowHeader("accountType", props);

	if (props.mode === "edit") {
		return (
			<InstitutionBaseRow
				{...commonProps}
				strategy={{
					mode: "edit",
					required: true,
					createControl: (id) => (
						<InstitutionDropdown<AccountType>
							value={accountType}
							onChange={props.onChange}
							aria-labelledby={id}
							{...getDisabledProps(props)}
							renderValue={(value) => t(`${value}`)}
						>
							{AccountTypeSchema.options.map((option) => (
								<CoreMenuItem key={option} value={option}>
									<ListItemText primary={t(option)} />
								</CoreMenuItem>
							))}
						</InstitutionDropdown>
					),
				}}
			/>
		);
	}

	return (
		<InstitutionBaseRow
			{...commonProps}
			strategy={{
				mode: "view",
				value: t(`${accountType}`),
				copyAriaLabel: t("copyAriaLabel"),
			}}
		/>
	);
}

type EditYuJaAccountRowProps = EditConsumerProps & {
	yujaAccount?: YujaAccount;
	allYujaAccounts?: YujaAccount[];
	onChange: (value: YujaAccount | null) => void;
};
type ViewYuJaAccountRowProps = ViewConsumerProps & {
	yujaAccountId?: string;
};

type InstitutionYuJaAccountRowProps = EditYuJaAccountRowProps | ViewYuJaAccountRowProps;

export function InstitutionYuJaAccountRow(props: InstitutionYuJaAccountRowProps) {
	const [searchInput, setSearchInput] = useState("");
	const { t, commonProps } = useInstitutionRowHeader("yujaAccount", props);

	if (props.mode === "edit") {
		const value = props.yujaAccount ?? null;
		const searchInputLower = searchInput.toLowerCase();
		const filteredAccounts =
			props.allYujaAccounts?.filter(
				(account) => searchInput === "" || account.name.toLowerCase().includes(searchInputLower),
			) ?? [];
		return (
			<InstitutionBaseRow
				{...commonProps}
				strategy={{
					mode: "edit",
					required: true,
					createControl: (id) => (
						<InstitutionDropdown<YujaAccount | null>
							value={value}
							onChange={props.onChange}
							aria-labelledby={id}
							{...getDisabledProps(props)}
							searchProps={{
								onChange: setSearchInput,
								value: searchInput,
								placeholder: t("placeholder"),
							}}
							renderValue={(account) => account?.name ?? t("placeholder")}
							unselectedPlaceholder={t("placeholder")}
						>
							{filteredAccounts.map((account) => (
								<CoreMenuItem key={account.yujaAccountId} value={account}>
									<ListItemText primary={account.name} />
								</CoreMenuItem>
							))}
						</InstitutionDropdown>
					),
				}}
			/>
		);
	}

	return (
		<InstitutionBaseRow
			{...commonProps}
			strategy={{
				mode: "view",
				value: props.yujaAccountId ?? t("placeholder"),
				copyAriaLabel: t("copyAriaLabel"),
			}}
		/>
	);
}
